import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import type { PricingDetails, StripeSessions, Tier } from '~/interfaces/subscriptions';
import type { UpgradePage } from '~/interfaces/upgrade_page';

import useIsMobile from '~/hooks/useIsMobile';

import TierCard from './TierCard';

// Sorts the tiers so that the recommended tier is in the middle when there are 3 tiers
const sortedTiers = (
  tiers: Tier[],
  recommendedPriceId: string,
  showRecommendedTierFirst: boolean,
  tiersOrder: UpgradePage['main_section']['tiers_order'],
): { enlarge: boolean; tiers: Tier[] } => {
  const recommendedTier = tiers.find((tier) => tier.prices.some((price) => price.id === recommendedPriceId));

  if (recommendedTier) {
    const filteredTiers = tiers.filter((tier) => tier !== recommendedTier);

    // If there's a recommended tier and it should be shown on top
    // Place the recommended tier on the top
    if (showRecommendedTierFirst || filteredTiers.length % 2 === 1) {
      return {
        enlarge: false,
        tiers: [recommendedTier, ...filteredTiers],
      };
    }

    // If there's a recommended tier and there are 3 in total
    // Place the recommended tier in the center and enlarge it
    if (tiers.length === 3) {
      return {
        enlarge: true,
        tiers: [filteredTiers[0], recommendedTier].concat(filteredTiers.slice(1)),
      };
    }
  }

  // Just render the tiers as returned in the response
  if (tiersOrder.length === 0) {
    return {
      enlarge: false,
      tiers,
    };
  }

  // Render the tiers based on the sorting order
  // ignore the tiers in sorted list that are
  // not a part of the tier response
  const sortedTiers = tiersOrder.reduce((pv, cv) => {
    const foundTiers = tiers.filter((tier) => tier.id === cv.id);
    return pv.concat(foundTiers);
  }, [] as Tier[]);

  // If the sorted tiers are less than the original tiers
  if (sortedTiers.length < tiers.length) {
    // Add the remaining tiers to the end of the sorted list
    const remainingTiers = tiers.filter((tier) => !sortedTiers.some((t) => t.id === tier.id));
    return {
      enlarge: false,
      tiers: [...sortedTiers, ...remainingTiers],
    };
  }

  return {
    enlarge: false,
    tiers: sortedTiers,
  };
};

const parseTiers = (tiers: Tier[], freeTierEnabled: boolean) => {
  if (tiers.length > 1) {
    return tiers;
  }

  // If splitting the tier and having the free tier card on the page would result in more than 3 cards, don't split
  const cardCount = tiers[0].prices.length + (freeTierEnabled ? 1 : 0);
  if (cardCount > 3) {
    return tiers;
  }

  if (tiers[0].prices.length === 1) {
    return tiers;
  }

  // Otherwise split one tier into multiple tiers
  return tiers[0].prices.map((price) => ({
    ...tiers[0],
    prices: [price],
  }));
};

interface Props {
  upgradePage: UpgradePage;
  pricingDetails: PricingDetails;
  priceId: string;
  setPriceId: (priceId: string) => void;
  isExternalCheckoutEnabled: boolean;
  isCollectingBillingAddress: boolean;
  sessionIds?: StripeSessions;
  areSessionsLoading: boolean;
  onContinue: () => void;
}

const PlanCards = ({
  upgradePage,
  pricingDetails,
  priceId,
  setPriceId,
  isExternalCheckoutEnabled = false,
  isCollectingBillingAddress = false,
  sessionIds,
  areSessionsLoading = false,
  onContinue,
}: Props) => {
  const { tiers: tiersData } = pricingDetails;
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const defaultCurrency = tiersData[0].prices[0].currency;
  const parsedTiers = parseTiers(tiersData, upgradePage.main_section.enable_free_card);
  const freeTierCta =
    upgradePage.main_section.free_card_cta_text === 'Keep Current Plan'
      ? t('upgrade.keep_current_plan')
      : upgradePage.main_section.free_card_cta_text;
  const { enlarge, tiers } = sortedTiers(
    [
      upgradePage.main_section.enable_free_card && {
        id: 'free',
        name: upgradePage.main_section.free_card_label_text,
        description: '',
        prices: [
          {
            id: 'free',
            amount_cents: 0,
            original_amount_cents: 0,
            currency: defaultCurrency,
            interval: 'monthly',
            denominator: '',
            cta: freeTierCta,
            features: upgradePage.main_section.free_card_features,
          },
        ],
      },
      ...parsedTiers,
    ].filter(Boolean) as Tier[],
    upgradePage.main_section.most_popular_price_id,
    upgradePage.main_section.show_popular_price_first && isMobile,
    upgradePage.main_section.tiers_order,
  );
  return (
    <div className="flex flex-wrap justify-center gap-14 lg:gap-x-6 lg:gap-y-10 xl:gap-x-14" key={`${isMobile}`}>
      {tiers.map((tier, index) => (
        <div
          key={`${tier.id}-${index}`}
          className={cx({
            'lg:py-4': enlarge && index !== 1,
          })}
        >
          <TierCard
            tier={tier}
            isExternalCheckoutEnabled={isExternalCheckoutEnabled}
            isCollectingBillingAddress={isCollectingBillingAddress}
            isSessionLoading={areSessionsLoading}
            sessionIds={sessionIds || {}}
            onPriceIdChange={setPriceId}
            onContinue={onContinue}
            selectedPriceId={priceId}
            upgradePage={upgradePage}
          />
        </div>
      ))}
    </div>
  );
};

export default PlanCards;
